import React, { useState, useEffect } from "react";
import { InfraVestLogo } from "@/components/InfraVestLogo";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";

const navLinks = [
  { label: "Vision", href: "#vision" },
  { label: "How it Works", href: "#how" },
  { label: "Owners", href: "#owners" },
  { label: "FAQ", href: "#faq" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToEarlyAccess = () => {
    const el = document.getElementById("early-access");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-10">
        <a href="#top" className="flex items-center" data-testid="header-logo-link">
          <InfraVestLogo size={28} />
        </a>

        <nav className="hidden items-center gap-8 md:flex" aria-label="primary">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm text-neutral-300 transition-colors hover:text-white"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Button
            data-testid="header-cta-join"
            onClick={scrollToEarlyAccess}
            className="hidden h-9 rounded-full bg-orange-500 px-5 text-sm font-medium text-neutral-950 hover:bg-orange-400 md:inline-flex"
          >
            Join Early Access
          </Button>
          <button
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="header-mobile-toggle"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-black/40 text-white md:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile sheet */}
      <div
        className={`md:hidden ${open ? "max-h-96" : "max-h-0"} overflow-hidden transition-[max-height] duration-500 border-b border-white/10 bg-black/85 backdrop-blur-xl`}
      >
        <nav className="mx-auto flex max-w-7xl flex-col gap-2 px-6 py-4">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-md px-3 py-2 text-sm text-neutral-300 hover:bg-white/5 hover:text-white"
            >
              {l.label}
            </a>
          ))}
          <Button
            data-testid="mobile-cta-join"
            onClick={scrollToEarlyAccess}
            className="mt-2 h-10 rounded-full bg-orange-500 px-5 font-medium text-neutral-950 hover:bg-orange-400"
          >
            Join Early Access
          </Button>
        </nav>
      </div>
    </header>
  );
};

export default Header;
